import { getReferralCreatorIdForApi } from '@/lib/analyticsAttribution';

const MONGO_OBJECT_ID = /^[0-9a-fA-F]{24}$/;

export type BookingCampaignVendor = 'gyg' | 'nuitee' | 'stay22';

export type BookingCampaignSurface =
  | 'place_modal'
  | 'results_experiences_tab'
  | 'place_map_sheet'
  | 'nav_rail'
  | 'itinerary_pin'
  | 'profile_cta'
  | 'list_timeline_card'
  | 'itinerary_timeline_card';

export type BookingCampaignRefType = 'post' | 'place';

export interface BuildBookingCampaignRefParams {
  userId?: string | null;
  refType?: BookingCampaignRefType;
  refId?: string | null;
  surface: BookingCampaignSurface;
  vendor: BookingCampaignVendor;
  timestamp?: number;
  /** Share-link creator id; defaults to getReferralCreatorIdForApi() when omitted. */
  referralCreatorId?: string | null;
}

export interface ParsedBookingCampaignRef {
  userId: string | null;
  refType: BookingCampaignRefType | null;
  refId: string | null;
  /** Share-link / first-touch creator id when present in `:rc:` segment. */
  referralCreatorId: string | null;
  /** Set when refType is post, or from legacy cmp format. */
  postId: string | null;
  /** Set when refType is place. */
  placeId: string | null;
  /** Legacy cmp only (Google place id segment). */
  googlePlaceId: string | null;
  surface: string | null;
  vendor: string | null;
  timestamp: number | null;
}

function isValidMongoId(id: string | null | undefined): id is string {
  return !!id && typeof id === 'string' && MONGO_OBJECT_ID.test(id);
}

/**
 * Resolves contextual attribution: post id when opened from a post/list/trip,
 * otherwise internal place (location) id for standalone place opens.
 */
export function resolveBookingCampaignRefContext(params: {
  postId?: string | null;
  placeId?: string | null;
}): { refType: BookingCampaignRefType; refId: string | null } {
  if (isValidMongoId(params.postId)) {
    return { refType: 'post', refId: params.postId };
  }
  if (isValidMongoId(params.placeId)) {
    return { refType: 'place', refId: params.placeId };
  }
  return { refType: 'post', refId: null };
}

/**
 * Builds a campaign reference string for affiliate attribution (GYG cmp param).
 * User id and context ref (post or place) lead the string so they survive GYG truncation.
 */
export function buildBookingCampaignRef(params: BuildBookingCampaignRefParams): string {
  const {
    userId,
    refType = 'post',
    refId,
    surface,
    vendor,
    timestamp = Date.now(),
  } = params;

  const userPart = isValidMongoId(userId) ? userId : 'NoUser';
  const refPart = isValidMongoId(refId) ? refId : 'NoRef';

  let cmp = `gt:u:${userPart}:r:${refType}:${refPart}:s:${surface}:v:${vendor}:ts:${timestamp}`;

  const referralCreatorId = params.referralCreatorId ?? getReferralCreatorIdForApi();
  if (isValidMongoId(referralCreatorId)) {
    cmp += `:rc:${referralCreatorId}`;
  }

  return cmp;
}

const NEW_CMP_REGEX =
  /^gt:u:(NoUser|[0-9a-fA-F]{24}):r:(post|place):(NoRef|[0-9a-fA-F]{24}):s:([^:]+):v:([^:]+):ts:(\d+)(?::rc:([0-9a-fA-F]{24}))?(?::widget:.+)?$/;

const LEGACY_CMP_REGEX =
  /^gt:(NoPost|[0-9a-fA-F]{24}):place:([^:]+):surface:([^:]+):vendor:([^:]+):ts:(\d+)(?::widget:.+)?$/;

const TRUNCATED_NEW_CMP_PREFIX =
  /^gt:u:(NoUser|[0-9a-fA-F]{24}):r:(post|place):(NoRef|[0-9a-fA-F]{24})/;

function parsedFromNewFormat(
  userPart: string,
  refType: BookingCampaignRefType,
  refPart: string,
  surface: string,
  vendor: string,
  tsPart: string,
  referralCreatorIdPart?: string,
): ParsedBookingCampaignRef {
  const refId = refPart === 'NoRef' ? null : refPart;
  return {
    userId: userPart === 'NoUser' ? null : userPart,
    refType,
    refId,
    referralCreatorId: isValidMongoId(referralCreatorIdPart) ? referralCreatorIdPart : null,
    postId: refType === 'post' ? refId : null,
    placeId: refType === 'place' ? refId : null,
    googlePlaceId: null,
    surface,
    vendor,
    timestamp: Number.parseInt(tsPart, 10),
  };
}

function parsedFromLegacyFormat(
  postPart: string,
  placePart: string,
  surface: string,
  vendor: string,
  tsPart: string,
): ParsedBookingCampaignRef {
  const postId = postPart === 'NoPost' ? null : postPart;
  const googlePlaceId = placePart === 'NoPlace' ? null : placePart;
  return {
    userId: null,
    refType: postId ? 'post' : null,
    refId: postId,
    referralCreatorId: null,
    postId,
    placeId: null,
    googlePlaceId,
    surface,
    vendor,
    timestamp: Number.parseInt(tsPart, 10),
  };
}

/**
 * Parses a campaign ref built by buildBookingCampaignRef (new or legacy format).
 * Returns null when the string does not match any known format.
 */
export function parseBookingCampaignRef(cmp: string): ParsedBookingCampaignRef | null {
  if (!cmp || typeof cmp !== 'string') {
    return null;
  }

  const newMatch = cmp.match(NEW_CMP_REGEX);
  if (newMatch) {
    const [, userPart, refType, refPart, surface, vendor, tsPart, referralCreatorIdPart] = newMatch;
    return parsedFromNewFormat(
      userPart,
      refType as BookingCampaignRefType,
      refPart,
      surface,
      vendor,
      tsPart,
      referralCreatorIdPart,
    );
  }

  const legacyMatch = cmp.match(LEGACY_CMP_REGEX);
  if (legacyMatch) {
    const [, postPart, placePart, surface, vendor, tsPart] = legacyMatch;
    return parsedFromLegacyFormat(postPart, placePart, surface, vendor, tsPart);
  }

  const truncatedMatch = cmp.match(TRUNCATED_NEW_CMP_PREFIX);
  if (truncatedMatch) {
    const [, userPart, refType, refPart] = truncatedMatch;
    const refId = refPart === 'NoRef' ? null : refPart;
    return {
      userId: userPart === 'NoUser' ? null : userPart,
      refType: refType as BookingCampaignRefType,
      refId,
      referralCreatorId: null,
      postId: refType === 'post' ? refId : null,
      placeId: refType === 'place' ? refId : null,
      googlePlaceId: null,
      surface: null,
      vendor: null,
      timestamp: null,
    };
  }

  return null;
}
